import { useCallback, useState, type ReactNode } from "react";
import {
  Box,
  DataTableDirectionIcon,
  DataTableGroupByDropdown,
  DataTableSortByDropdown,
  VisualPicker,
  Input,
  OptionPanelActionRow,
  OptionPanelNavRow,
  OptionPanelRow,
  OptionPanelSection,
  OptionPanelSeparator,
  Switch,
  Text,
  type Color,
  type DataTableGroupSortDirection,
  type GlyphName,
  type IconName,
} from "@salestrak/ui";

import type { SortDirection } from "#app/lib/data";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { countActiveFilters } from "#app/lib/filters/build-where";
import { useResetOnChange } from "#app/lib/hooks/use-reset-on-change";
import { useTrackViewOption, useViews } from "#app/lib/views/view-context";
import {
  AnalyticsContext,
  IssueEvent,
  ViewEvent,
  useAnalytics,
} from "#app/lib/analytics";
import { useDashboardViewFavorite } from "#app/components/dashboards/use-dashboard-favorites";
import { DisabledReason, NO_PERMISSION } from "#app/components/menus/disabled-reason";
import { CollapseEmptyStagesRow } from "./collapse-empty-stages-row";
import { DeleteViewDialog } from "./delete-view-dialog";
import { useCopyViewLink } from "./use-copy-view-link";
import { viewTypeSeed, viewVisual } from "./view-type-meta";

/**
 * The panel's landing page: the view's identity, its display toggles, the
 * rows that drill into Fields and Filters, the Actions group that pins the
 * view or makes it the default, and Delete alone at the bottom.
 *
 * Identity commits IMMEDIATELY — a rename or a new glyph is a property of
 * the view, not of the display options, so it never waits behind Save. The
 * toggles below it all write the draft config and settle on Save.
 *
 * Two kinds of row, two gates. A DRAFT row (display toggles, Fields, Filters,
 * Group by, Sort by, the More options page) is open to every viewer: it
 * changes what the person is looking at, and without `create.views` the
 * change is simply temporary — there is no Save control to commit it, and
 * the Discard row below puts the view back. A SHARED-ROW property (the
 * identity, Pin, Set as default, Delete) writes the view for everyone at
 * once, so it sits behind `create.views`: the switches lock and say why in
 * a tooltip; the identity editors, which have no locked reading worth
 * showing, are not rendered at all.
 */

/** The grouping dimensions the mounted view offers. The body owns them: a
 *  list's are its columns, a board's are its columns-plus-swimlanes. */
export type ViewOptionsGroupBy = {
  labels: Record<string, string>;
  icons?: Partial<Record<string, IconName>>;
  /** A board cannot render ungrouped — its columns ARE the grouping. */
  requireGrouping?: boolean;
};

/** The sort fields the mounted view offers. Every view can render unsorted —
 *  rows fall back to their natural order — so there is no require flag. */
export type ViewOptionsSortBy = {
  labels: Record<string, string>;
  icons?: Partial<Record<string, IconName>>;
  /** Each field's natural direction, applied when THAT field is picked —
   *  the direction control still overrides freely afterwards. A field with
   *  no entry keeps whatever direction is already set. */
  defaultDirections?: Partial<Record<string, SortDirection>>;
};

export type ViewOptionsMainProps = {
  groupBy?: ViewOptionsGroupBy | undefined;
  sortBy?: ViewOptionsSortBy | undefined;
  /** The body's Display rows — a board's card size and layout modes. A body
   *  that passes them owns the whole group above the More options door and
   *  seats `CollapseEmptyStagesRow` among them itself; omitted, the group
   *  holds that shared row alone. */
  display?: ReactNode | undefined;
  /** How many fields the host's Fields editor shows (the title field aside).
   *  Present, the row has somewhere to go and reads the count out. */
  fieldsShownCount: number | undefined;
  /** Body-owned option groups rendered between the shared groups and Actions
   *  — a dashboard's Viewing/Layout sections. The node carries its own
   *  separators BETWEEN its sections; this page owns the ones around it. */
  sections?: ReactNode | undefined;
  onOpenFields: () => void;
  /** Opens the Filters page. The row renders only where the host has an
   *  `IssueFilterProvider` to read the filter config from. */
  onOpenFilters: () => void;
  /** Opens the More options page — the rarer display toggles and the reset
   *  action, a layer deeper the way Fields and Filters are. */
  onOpenMore: () => void;
};

export function ViewOptionsMain({
  groupBy,
  sortBy,
  display,
  fieldsShownCount,
  sections,
  onOpenFields,
  onOpenFilters,
  onOpenMore,
}: ViewOptionsMainProps) {
  const {
    scope,
    views,
    activeView,
    config,
    setConfig,
    canManage,
    dirtyKeys,
    discardChanges,
    setPinned,
    updateView,
    trackView,
    setDefaultView,
  } = useViews();
  const trackOption = useTrackViewOption();
  const { track } = useAnalytics();
  // A workspace dashboard is one page, not one tab of a strip: it has no
  // strip to pin to, no scope to open on, and no last-view floor (the
  // directory is fine empty). The server skips the floor for it too.
  const isWorkspaceDashboard = scope.kind === "workspace";
  // A team's record tab is a BUILT-IN view: the strip names it (not the row),
  // holds no pins or default, and never deletes it — so the panel offers
  // only what the tab can actually change (fields, filters, grouping, sort,
  // the More options), never a control that would sit dead.
  const isTeamView = scope.kind === "team";
  const filterCtx = useIssueFiltersOptional();

  const [name, setName] = useState(activeView?.name ?? "");
  useResetOnChange(activeView?.id ?? null, () =>
    setName(activeView?.name ?? ""),
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const copyLink = useCopyViewLink(activeView, "panel");
  // A dashboard's star — the viewer's own, so it sits with the view's actions
  // rather than the shared-row switches above, and never behind `create.views`.
  const favorite = useDashboardViewFavorite(activeView, scope, "panel");

  const commitName = useCallback(() => {
    if (!activeView) return;
    const next = name.trim();
    // An empty box is a slip, not a rename — restore what the view is called.
    if (!next) {
      setName(activeView.name);
      return;
    }
    if (next === activeView.name) return;
    void updateView(activeView.id, { name: next }).then((saved) => {
      if (saved) trackView(ViewEvent.Rename, saved, { via: "panel" });
    });
  }, [activeView, name, trackView, updateView]);

  if (!activeView) return null;

  const visual = viewVisual(activeView);
  const seed = viewTypeSeed(activeView);
  const visualIsSeed =
    visual.icon === seed.icon && visual.color === seed.color;
  // The shared Display and Layout groups exist to edit the view's issue-query
  // config. A body that publishes none of their content (a dashboard) gets
  // neither group — its `sections` carry the panel's middle instead. The
  // Filters row rides the same gate: a dashboard's own sections carry their
  // own Filters row, which drills into the same Filters page — the host folds
  // the saved filters into every widget's data.
  const hasFields = fieldsShownCount !== undefined;
  const hasConfigGroups = Boolean(groupBy || sortBy || display || hasFields);
  // Both of the view's saved editors count on the row — they apply together.
  const viewFilterCount =
    countActiveFilters(config.filters) +
    countActiveFilters(config.advancedFilters);
  // The delete floor is one view of each REQUIRED type (List, Board), not
  // the seeded rows: a built-in can go once a same-type sibling exists, and a
  // dashboard can always go. The server enforces the same floor, so a stale
  // count still fails safely.
  const isLastOfType =
    !isWorkspaceDashboard &&
    viewTypeSeed(activeView).required &&
    views.filter((row) => row.type === activeView.type).length <= 1;
  // Most-specific reason first: a viewer with no grant gets the permission
  // line whatever the view's role is.
  const deleteDisabledReason = !canManage
    ? NO_PERMISSION
    : activeView.isDefault
      ? "Can't delete the default view"
      : isLastOfType
        ? `Must have at least one ${viewTypeSeed(activeView).label} view`
        : null;
  // The dimension the view is grouped by, named on its row. A list can render
  // flat, and the dropdown calls that state "None".
  // The table's label map spells `none` as an empty string (the toolbar chip
  // wears its own glyph there), so the row names it itself.
  const groupByLabel =
    config.groupBy === "none" ? "None" : groupBy?.labels[config.groupBy] || "None";
  // Unsorted is a real state, not a missing one — the rows keep their natural
  // order — so its row reads "None (default)" rather than sitting empty.
  const sortByLabel =
    config.sortBy === "none"
      ? "None (default)"
      : (sortBy?.labels[config.sortBy] ?? config.sortBy);
  // A row's readout states the field AND its order, the way the toolbar chip
  // does: the arrow rides beside the value while a field is set, and drops
  // with it — an unset field has no order to read.
  const fieldReadout = (
    label: string,
    direction: DataTableGroupSortDirection | undefined,
    set: boolean,
  ) => (
    <Box align="center" direction="row" gap="xs">
      <Text size="xs" variant="faint">
        {label}
      </Text>
      {set ? (
        <DataTableDirectionIcon
          color="faint"
          direction={direction ?? "asc"}
          labelled
        />
      ) : null}
    </Box>
  );

  // The shared-row switches lock for a viewer without the grant and say why
  // on hover — the wrapper is the tooltip's trigger, so the row underneath
  // drops its pointer events (a disabled switch would swallow the hover).
  const lockedRow = (row: ReactNode) =>
    canManage ? (
      row
    ) : (
      <DisabledReason reason={NO_PERMISSION}>
        <Box fill="width" pointerEvents="none">
          {row}
        </Box>
      </DisabledReason>
    );

  return (
    <Box direction="column">
      {/* Identity — the glyph and the name, on the panel's own top rung. Only
          for a viewer who may rename: the tab already names the view, so a
          locked copy of the name here would say nothing the strip does not. */}
      {canManage && !isTeamView ? (
        <>
          <Box
            direction="row"
            align="center"
            gap="sm"
            paddingX="lg"
            paddingTop="base"
            paddingBottom="base"
          >
            <VisualPicker
              size="sm"
              variant="solid"
              icon={visual.icon}
              defaultIcon={seed.icon}
              color={visual.color}
              onIconChange={(icon: GlyphName) => {
                void updateView(activeView.id, {
                  icon: { icon, color: visual.color },
                });
              }}
              onColorChange={(color: Color) => {
                void updateView(activeView.id, {
                  icon: { icon: visual.icon, color },
                });
              }}
              // Back to the type's seed glyph + color in ONE write (the name
              // stays), so the tab returns to its structural tile
              // (`viewTabVisual`). Offered only once either one differs.
              onReset={
                visualIsSeed
                  ? undefined
                  : () => {
                      void updateView(activeView.id, {
                        icon: { icon: seed.icon, color: seed.color },
                      });
                      trackOption("icon", { value: "reset" });
                    }
              }
            />
            <Box direction="column" fill="flex" minWidth={0}>
              <Input
                value={name}
                aria-label="View name"
                onChange={(event) => setName(event.target.value)}
                onBlur={commitName}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") setName(activeView.name);
                }}
              />
            </Box>
          </Box>

          <OptionPanelSeparator />
        </>
      ) : null}

      {/* The landing page's groups carry NO titles: the hairlines between
          them are the grouping, and a row on this layer says what it is by
          its label. The deeper pages (More options, Filters) keep theirs —
          a titled group there names a rule the rows share. */}
      {hasConfigGroups ? (
        <>
          {/* The Display rows, then one door. The rarer settings (which
              closed projects show, the Only me default, reset) live a layer
              deeper, the way Fields and Filters do. */}
          <OptionPanelSection>
            {/* The one Display switch a body opens with. A workflow's list
                groups by stage, so it is Collapse empty stages; a team's
                spans workflows (never stage-grouped), so its switch is
                whether Projects only SHARED with the team join the ones
                assigned to it. */}
            {display ??
              (isTeamView ? (
                <OptionPanelRow label="Show shared Projects">
                  <Switch
                    size="sm"
                    checked={config.showShared}
                    onCheckedChange={(checked) => {
                      setConfig({ showShared: !!checked });
                      trackOption("showShared", { on: !!checked });
                    }}
                  />
                </OptionPanelRow>
              ) : (
                <CollapseEmptyStagesRow />
              ))}
            <OptionPanelNavRow label="More options" onClick={onOpenMore} />
          </OptionPanelSection>

          <OptionPanelSeparator />

          {/* Fields and Filters lead, then the grouping and the sort — the
              order a reader shapes a view in (what shows, which rows, how
              they fall) — and only the rows this body offers. */}
          <OptionPanelSection>
            {hasFields ? (
              <OptionPanelNavRow
                label="Fields"
                icon="square-pen"
                value={`${fieldsShownCount} shown`}
                onClick={onOpenFields}
              />
            ) : null}
            {filterCtx ? (
              // Drills into the Filters page, which edits this view's filters
              // — the layer Save commits, not the toolbar chip's temporary
              // pool. The count is the draft's.
              <OptionPanelNavRow
                label="Filters"
                icon="filter"
                value={viewFilterCount > 0 ? viewFilterCount : "None"}
                onClick={onOpenFilters}
              />
            ) : null}
            {groupBy ? (
              // The row itself opens the editor — pressing anywhere across it,
              // not a control parked on its trailing edge — and the active
              // dimension reads in place, the way every other row of this list
              // reports its value.
              <DataTableGroupByDropdown
                groupByField={config.groupBy}
                setGroupByField={(field) => {
                  setConfig({ groupBy: field });
                  track({
                    context: AnalyticsContext.Issue,
                    event: IssueEvent.ChangeGrouping,
                    data: {
                      view: activeView.type,
                      scope: scope.kind,
                      dimension: field,
                      via: "panel",
                    },
                  });
                }}
                groupByLabels={groupBy.labels}
                groupByIcons={groupBy.icons}
                groupSortDirection={config.groupDirection}
                setGroupSortDirection={(direction) =>
                  setConfig({ groupDirection: direction })
                }
                requireGrouping={groupBy.requireGrouping}
                trigger={
                  <OptionPanelNavRow
                    label="Group by"
                    icon="layers"
                    value={fieldReadout(
                      groupByLabel,
                      config.groupDirection,
                      config.groupBy !== "none",
                    )}
                  />
                }
              />
            ) : null}
            {sortBy ? (
              <DataTableSortByDropdown
                sortByField={config.sortBy}
                setSortByField={(field) => {
                  const preferred = sortBy.defaultDirections?.[field];
                  setConfig(
                    preferred
                      ? { sortBy: field, sortDirection: preferred }
                      : { sortBy: field },
                  );
                  track({
                    context: AnalyticsContext.Issue,
                    event: IssueEvent.ChangeSorting,
                    data: {
                      view: activeView.type,
                      scope: scope.kind,
                      field,
                      via: "panel",
                    },
                  });
                }}
                sortByLabels={sortBy.labels}
                sortByIcons={sortBy.icons}
                sortDirection={config.sortDirection}
                setSortDirection={(direction) =>
                  setConfig({ sortDirection: direction })
                }
                trigger={
                  <OptionPanelNavRow
                    label="Sort by"
                    icon="arrow-up-down"
                    value={fieldReadout(
                      sortByLabel,
                      config.sortDirection,
                      config.sortBy !== "none",
                    )}
                  />
                }
              />
            ) : null}
          </OptionPanelSection>

          <OptionPanelSeparator />
        </>
      ) : null}

      {sections ? (
        <>
          {sections}
          <OptionPanelSeparator />
        </>
      ) : null}

      {/* Pin and default are properties of the view, like its name: each
          commits at once and never waits behind Save, and each writes the
          shared row behind `create.views`. At most one default per scope:
          ON moves the role here (the previous default's goes off with it);
          OFF leaves the scope with none, and it opens on its first view. */}
      {isWorkspaceDashboard || isTeamView ? null : (
        <>
          <OptionPanelSection>
          {lockedRow(
            <OptionPanelRow label="Pin view" disabled={!canManage}>
              <Switch
                size="sm"
                checked={activeView.isPinned}
                disabled={!canManage}
                onCheckedChange={(checked) => {
                  void setPinned(activeView.id, checked);
                }}
              />
            </OptionPanelRow>,
          )}
          {/* The default is the FIRST-visit landing: the bare workflow URL
              reopens whichever view a person last had here (`useLastView`),
              and falls back to this one. Said behind the row's info glyph, or
              an admin who sets it and then watches teammates land elsewhere
              files it as broken. */}
          {lockedRow(
            <OptionPanelRow
              label="Set as default view"
              hint="Where people land the first time they open this workflow"
              disabled={!canManage}
            >
              <Switch
                size="sm"
                checked={activeView.isDefault}
                disabled={!canManage}
                onCheckedChange={(checked) => {
                  void setDefaultView(activeView.id, checked);
                }}
              />
            </OptionPanelRow>,
          )}
          </OptionPanelSection>
          {/* Closes this group only when it rendered: the group above already
              closed itself, and a second hairline here would double it. */}
          <OptionPanelSeparator />
        </>
      )}

      {/* The view's own actions close the panel: the link, then Delete on
          the last rung, apart from the rows that only change how the view
          behaves. The default view is where the scope opens, so it stays
          until the role moves off it — the server refuses the delete too. A
          disabled row says why in a tooltip (the menu grammar); the row drops
          pointer events so the wrapper Box catches the hover a disabled
          button would swallow. */}
      <OptionPanelSection>
        {/* A viewer without `create.views` has no Save control, so their
            draft edits are temporary and this is their way back. Present
            only while there is something to put back; a manager's Discard
            lives on the toolbar's Save control. */}
        {!canManage && dirtyKeys.length > 0 ? (
          <OptionPanelActionRow
            label="Discard changes"
            icon="reload"
            onClick={discardChanges}
          />
        ) : null}
        {favorite ? (
          <OptionPanelActionRow
            label={favorite.favorited ? "Remove from favorites" : "Add to favorites"}
            icon={favorite.favorited ? "star-filled" : "star"}
            onClick={favorite.toggle}
          />
        ) : null}
        <OptionPanelActionRow
          label="Copy link to view"
          icon="link"
          onClick={copyLink}
        />
        {isTeamView ? null : deleteDisabledReason ? (
          <DisabledReason reason={deleteDisabledReason}>
            <OptionPanelActionRow
              label="Delete view"
              icon="trash"
              disabled
              pointerEvents="none"
              onClick={() => setConfirmDelete(true)}
            />
          </DisabledReason>
        ) : (
          <OptionPanelActionRow
            label="Delete view"
            icon="trash"
            onClick={() => setConfirmDelete(true)}
          />
        )}
      </OptionPanelSection>

      <DeleteViewDialog
        view={activeView}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
      />
    </Box>
  );
}
